/**
 * BranchesSettingsSection — injected into Settings via render-prop
 * (Prompt 11F.8) — Settings must not import from Calendar directly (ESLint
 * feature-boundary rule), same pattern as LeavePolicySettingsSection.
 *
 * The mock Settings "Branches" tab showed two hardcoded cards with an
 * inert "Add Branch"/"Edit" button. Branches already have a full CRUD
 * backend (`GET/POST/PATCH /v1/calendar/branches`, used today only to
 * populate the Calendar filter dropdown) — this wires the existing model
 * up to real add/edit UI instead of inventing a new one.
 */

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input, Modal } from '@/components/common';
import { useTenant } from '@core/tenant/TenantContext';
import {
  useBranches,
  useCreateBranch,
  useUpdateBranch,
  type BranchResponse,
} from '@features/calendar/api/branches';
import type { ApiError } from '@core/api/client';

const ReadOnlyBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BranchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.md};
`;

interface FormValues {
  name: string;
  address: string;
  timezone: string;
  status: 'active' | 'inactive';
}

const EMPTY: FormValues = { name: '', address: '', timezone: 'Asia/Kolkata', status: 'active' };

function toFormValues(b: BranchResponse | null): FormValues {
  if (!b) return EMPTY;
  return { name: b.name, address: b.address ?? '', timezone: b.timezone, status: b.status };
}

export const BranchesSettingsSection: React.FC = () => {
  const { isAllMode } = useTenant();
  const { data: branchesResult, isLoading } = useBranches();
  const createMut = useCreateBranch();
  const updateMut = useUpdateBranch();

  const [editing, setEditing] = useState<BranchResponse | null | 'new'>(null);
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({ defaultValues: EMPTY });

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? null : editing));
  }, [editing, reset]);

  const branches = branchesResult?.items ?? [];

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (editing === 'new') {
        await createMut.mutateAsync(values);
        toast.success('Branch created.');
      } else if (editing) {
        await updateMut.mutateAsync({ id: editing.id, body: values });
        toast.success('Branch updated.');
      }
      setEditing(null);
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not save branch.');
    }
  });

  return (
    <Card padding="lg" data-testid="candeza-settings-section-branches">
      <CardHeader>
        <CardTitle>Branches</CardTitle>
        <Button
          size="sm"
          disabled={isAllMode}
          onClick={() => setEditing('new')}
          data-testid="candeza-branches-add-btn"
        >
          Add Branch
        </Button>
      </CardHeader>

      {isAllMode && (
        <ReadOnlyBanner data-testid="candeza-branches-all-mode-banner">
          Select a school to manage Branches.
        </ReadOnlyBanner>
      )}

      <div style={{ marginTop: 16 }}>
        {isLoading && <p style={{ color: '#64748b' }}>Loading branches…</p>}
        {!isLoading && branches.length === 0 && (
          <p style={{ color: '#64748b' }}>No branches yet.</p>
        )}
        {branches.map((b) => (
          <Card
            key={b.id}
            padding="md"
            style={{ marginBottom: 12 }}
            data-testid={`candeza-branch-row-${b.id}`}
          >
            <BranchRow>
              <div>
                <strong>{b.name}</strong>
                {b.status === 'inactive' && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#94a3b8' }}>inactive</span>
                )}
                {b.address && (
                  <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>{b.address}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={isAllMode}
                onClick={() => setEditing(b)}
                data-testid={`candeza-branch-edit-${b.id}`}
              >
                Edit
              </Button>
            </BranchRow>
          </Card>
        ))}
      </div>

      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Branch' : 'Edit Branch'}
        data-testid="candeza-branch-form-modal"
      >
        <form onSubmit={onSubmit} noValidate>
          <FormGrid>
            <Input
              label="Name *"
              {...register('name', { required: 'Required' })}
              data-testid="candeza-branch-form-name"
            />
            <Input label="Address" {...register('address')} data-testid="candeza-branch-form-address" />
            <Input
              label="Timezone"
              {...register('timezone', { required: 'Required' })}
              data-testid="candeza-branch-form-timezone"
            />
          </FormGrid>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={formState.isSubmitting} data-testid="candeza-branch-form-submit">
              {formState.isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
};

export default BranchesSettingsSection;
